import { KeyboardEvent, RefObject, useEffect, useRef } from 'react'

/**
 * Keeps a stepper's body focused across step changes, so Enter keeps reaching it.
 *
 * Advancing unmounts the field that was focused, and the browser drops focus back onto `document.body`
 * — outside the flyout, where a handler bound to the body of the step never sees another keypress. The
 * review step makes that plain: it holds no field at all, so without this the key that walked the user
 * to the last step cannot press the button on it.
 *
 * `tabIndex={-1}` on the element is what makes it focusable at all; it stays out of the tab order.
 */
export const useStepperFocus = (currentStep: number): RefObject<HTMLDivElement | null> => {
  const bodyRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    // Never steal focus from a field the step put it in — an autofocused input outranks the container.
    if (!bodyRef.current?.contains(document.activeElement)) {
      bodyRef.current?.focus({ preventScroll: true })
    }
  }, [currentStep])
  return bodyRef
}

/**
 * Enter, inside a stepper, means "I am done with this step".
 *
 * A stepper is one form split across screens, so the reflex Enter carries in every other form — send
 * what I just typed — is the same reflex here. A Next button reachable only with the mouse is what makes
 * a three-step flyout feel longer than the single form it replaced.
 *
 * What Enter must never do is steal a keypress a control was already using: a textarea is opening a
 * line, a link or a button is being pressed, an open dropdown is picking the highlighted option, and
 * anything that already called `preventDefault` has handled it. A card that carries a checked state is
 * the exception — it is a choice, not an action, so Enter on a picked one means "yes, that one", which
 * is exactly the step being validated.
 *
 * `validate` is the step's own button, disabled state included: this decides only that Enter was asked,
 * never that the step may pass.
 */
export const validateStepOnEnter =
  (validate: () => void) =>
  (event: KeyboardEvent<HTMLElement>): void => {
    if (event.key !== 'Enter' || event.shiftKey || event.altKey || event.metaKey || event.ctrlKey || event.defaultPrevented) {
      return
    }
    const target = event.target as HTMLElement | null
    if (!target) {
      return
    }
    // An open combobox is answering Enter with its highlighted option — a `Select` mid-pick, never a step.
    if (target.closest('[aria-expanded="true"]')) {
      return
    }
    const handledElsewhere = target.closest('textarea, button, a, [role="button"], [role="link"]')
    if (handledElsewhere && !handledElsewhere.matches('[role="radio"], [aria-checked]')) {
      return
    }
    event.preventDefault()
    validate()
  }
